import React, { useEffect, useState } from 'react';
import axios from 'axios'; // Import axios for making HTTP requests
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import portcoRules from '../Data'; // Ensure this import is correct
import { useMsal } from '@azure/msal-react';

const Calculator = () => {
    const { instance, accounts } = useMsal();
    const [ctc, setCtc] = useState('');
    const [portfolioCompany, setPortfolioCompany] = useState('');
    const [selectedRate, setSelectedRate] = useState(null); // Initialize as null
    const [result, setResult] = useState(null);
    const [error, setError] = useState(''); // State for error message
    const [companies, setCompanies] = useState([]); // Initialize with an empty array
    const [portcoRules, setPortcoRules] = useState([]); // State to hold fetched data
    const [fxRates, setFxRates] = useState([]); // State to hold FX rates
    const [loading, setLoading] = useState(false); // State for loading indicator
    const [initializing, setInitializing] = useState(true); // Add this new state
    const [isAuthenticating, setIsAuthenticating] = useState(false); // Add this new state

    useEffect(() => {
        const fetchCompanies = async (accessToken) => {
            try {
                let allCompanies = [];
                let nextLink = `https://graph.microsoft.com/v1.0/sites/2408e5d2-699d-43ff-a005-03564e4f9b59/lists/7a56d189-4138-48e1-af6f-8425938f9706/items?expand=fields`;

                do {
                    const response = await axios.get(nextLink, {
                        headers: {
                            'Authorization': `Bearer ${accessToken}`,
                        },
                    });

                    // Map the companies directly
                    const mappedCompanies = response.data.value.map(item => ({
                        id: item.id,
                        portco_name: item.fields.portco_namek, // Using portco_namek for now
                    }));

                    allCompanies = allCompanies.concat(mappedCompanies);
                    console.log('Mapped companies:', mappedCompanies);

                    // Check for next page
                    nextLink = response.data['@odata.nextLink'] || null;
                } while (nextLink);

                // Sort companies alphabetically
                allCompanies.sort((a, b) => a.portco_name.localeCompare(b.portco_name));
                
                // Set companies state
                setCompanies(allCompanies);
                console.log('All companies (A to Z):', allCompanies);
                
                return allCompanies;
            } catch (error) {
                console.error('Error fetching companies:', error);
                return [];
            }
        };

        const fetchPortcoRules = async (accessToken, companiesList) => {
            try {
                let allItems = [];
                let nextLink = `https://graph.microsoft.com/v1.0/sites/2408e5d2-699d-43ff-a005-03564e4f9b59/lists/ca9209f9-d34f-40ba-b91a-6bacf406d6e3/items?expand=fields`;

                do {
                    const response = await axios.get(nextLink, {
                        headers: {
                            'Authorization': `Bearer ${accessToken}`,
                        },
                    });

                    console.log('Raw rules response:', response.data);

                    const mappedItems = response.data.value.map(item => {
                        // Find the matching company using the correct lookup ID field name
                        const matchingCompany = companiesList.find(c => 
                            c.id === item.fields.portco_namLookupId
                        );
                        console.log('Mapping rule:', {
                            ruleId: item.id,
                            portcoLookupId: item.fields.portco_namLookupId,
                            matchingCompany: matchingCompany,
                            fields: item.fields // Debug log to see all fields
                        });

                        if (!matchingCompany) {
                            console.warn('No matching company found for lookup ID:', item.fields.portco_namLookupId);
                        }

                        return {
                            id: item.id,
                            portcoLookupId: item.fields.portco_namLookupId,
                            portco_name: matchingCompany ? matchingCompany.portco_name : 'Unknown Company',
                            markup: parseFloat(item.fields.markup) || 0,
                            fixed_recharge: parseFloat(item.fields.fixed_recharge) || 0,
                            recharge_subject_to_markup: parseFloat(item.fields.recharge_subject_to_markup) || 0,
                            valid_from: new Date(item.fields.valid_from),
                        };
                    });

                    allItems = allItems.concat(mappedItems);
                    console.log('Mapped Rules:', mappedItems);
                    nextLink = response.data['@odata.nextLink'] || null;
                } while (nextLink);

                setPortcoRules(allItems);
                console.log('Final portco rules:', allItems);
            } catch (error) {
                console.error('Error fetching portco rules:', error);
            }
        };

        const fetchFxRates = async (accessToken) => {
            try {
                let allRates = [];
                let nextLink = `https://graph.microsoft.com/v1.0/sites/2408e5d2-699d-43ff-a005-03564e4f9b59/lists/b73296ac-53cd-4d97-b942-7797f03d555b/items?expand=fields`; // Fetch FX rates

                do {
                    const response = await axios.get(nextLink, {
                        headers: {
                            'Authorization': `Bearer ${accessToken}`, // Use the user's access token
                        },
                    });

                    // Debugging: Log the response data
                    console.log('Response from fetchFxRates:', response.data);

                    // Map the response data to the desired structure
                    const mappedRates = response.data.value.map(item => ({
                        id: item.id,
                        rate_pair: item.fields.rate_pair,
                        fx_rate: parseFloat(item.fields.fx_rate),
                        valid_from: new Date(item.fields.valid_from),
                    }));

                    // Add console logs right after mapping to verify the data
                    console.log('Mapped Rates:', mappedRates);

                    // Add the current page of items to the allRates array
                    allRates = allRates.concat(mappedRates);

                    // Check if there is a nextLink for pagination
                    nextLink = response.data['@odata.nextLink'] || null; // Get the next link for pagination
                } while (nextLink); // Continue fetching until there are no more pages

                // Filter and get unique rate_pairs with the most recent valid_from date
                const today = new Date();
                const uniqueRates = {};

                allRates.forEach(rate => {
                    if (rate.valid_from <= today && (!rate.expiry_date || rate.expiry_date > today)) { // Check if valid_from is today or earlier and either no expiry or not expired
                        if (!uniqueRates[rate.rate_pair] || uniqueRates[rate.rate_pair].valid_from < rate.valid_from) {
                            uniqueRates[rate.rate_pair] = rate; // Store the rate if it's the most recent
                        }
                    }
                });

                // Convert the uniqueRates object back to an array
                const uniqueFxRates = Object.values(uniqueRates);

                // Set the fetched FX rates to state
                setFxRates(uniqueFxRates);
                console.log('FX Rates state after update:', uniqueFxRates);
                // Optionally, set the default selected rate if needed
                if (uniqueFxRates.length > 0) {
                    setSelectedRate(uniqueFxRates[0]); // Set the first rate as default
                }

                // Debugging: Log the FX rates
                console.log('Unique FX Rates:', uniqueFxRates);
            } catch (error) {
                console.error('Error fetching FX rates from SharePoint:', error);
            }
        };

        const loginAndFetchData = async () => {
            try {
                setLoading(true);
                setError('');

                // Get current accounts
                const currentAccounts = accounts;
                
                // If no accounts, start login
                if (currentAccounts.length === 0 && !isAuthenticating) {
                    try {
                        setIsAuthenticating(true); // Set flag before starting auth
                        await instance.loginRedirect({
                            scopes: ['User.Read', 'Sites.Read.All']
                        });
                        return; // Return as we're redirecting
                    } catch (loginError) {
                        if (loginError.errorCode !== 'interaction_in_progress') {
                            console.error('Login redirect error:', loginError);
                            setError('Login failed. Please try again.');
                        }
                        return;
                    } finally {
                        setIsAuthenticating(false); // Reset flag
                    }
                }

                // We have an account, try to get token silently
                try {
                    const tokenResponse = await instance.acquireTokenSilent({
                        scopes: ['User.Read', 'Sites.Read.All'],
                        account: currentAccounts[0]
                    });

                    // If we got here, we have a token - fetch data
                    const companies = await fetchCompanies(tokenResponse.accessToken);
                    await fetchPortcoRules(tokenResponse.accessToken, companies);
                    await fetchFxRates(tokenResponse.accessToken);

                } catch (tokenError) {
                    console.error('Token acquisition error:', tokenError);
                    if (tokenError instanceof InteractionRequiredAuthError && !isAuthenticating) {
                        setIsAuthenticating(true);
                        await instance.loginRedirect({
                            scopes: ['User.Read', 'Sites.Read.All']
                        });
                        setIsAuthenticating(false);
                    } else {
                        setError('Failed to get access token. Please refresh the page.');
                    }
                }

            } catch (error) {
                console.error('Authentication error:', error);
                setError('Authentication failed. Please refresh the page and try again.');
            } finally {
                setLoading(false);
                setInitializing(false);
            }
        };

        loginAndFetchData();
    }, [instance, accounts, isAuthenticating]);

    // Update the return statement to handle the initializing state
    if (initializing) {
        return (
            <div className="container">
                <img src="/logo.png" alt="Blenheim Chalcot" style={{ width: '50%', marginBottom: '20px' }} />
                <h2>Calculate Cost to PortCo</h2>
                <div className="loading-message">
                    Initializing application...
                </div>
            </div>
        );
    }

    const calculate = () => {
        setError('');
        
        // Add debugging logs
        console.log('Current State:', {
            selectedCompany: portfolioCompany,
            allRules: portcoRules,
            selectedRate: selectedRate
        });

        // Validate inputs
        if (!ctc && !portfolioCompany) {
            setError('Please enter a CTC and select a Portfolio Company.');
            return;
        } else if (!ctc) {
            setError('Please enter a CTC.');
            return;
        } else if (!portfolioCompany || portfolioCompany === "") {
            setError('Please select a Portfolio Company.');
            return;
        } else if (!selectedRate) {
            setError('Please select an FX Rate.');
            return;
        }

        const ctcInGBP = parseFloat(ctc.replace(/,/g, '')) / selectedRate.fx_rate;

        // Find the most recent rule for the selected company
        const today = new Date();
        
        // Debug log before filtering
        console.log('Looking for rules with company name:', portfolioCompany);
        console.log('Available rules:', portcoRules);
        
        const companyRules = portcoRules.filter(rule => {
            console.log('Comparing:', {
                ruleCompany: rule.portco_name,
                selectedCompany: portfolioCompany,
                matches: rule.portco_name === portfolioCompany,
                validFrom: rule.valid_from <= today
            });
            return rule.portco_name === portfolioCompany && rule.valid_from <= today;
        });

        console.log('Filtered company rules:', companyRules);

        if (!companyRules || companyRules.length === 0) {
            setError('No rules found for the selected company.');
            return;
        }

        // Get the most recent rule
        const selectedRule = companyRules.reduce((latest, current) => 
            latest.valid_from > current.valid_from ? latest : current
        );

        console.log('Selected Rule:', selectedRule);

        const markupCost = (ctcInGBP + selectedRule.recharge_subject_to_markup) * selectedRule.markup;
        const costToPortCo = ctcInGBP + markupCost + selectedRule.fixed_recharge;

        setResult({
            costToPortCo,
            ctcInGBP,
            markupCost,
            fixedRecharge: selectedRule.fixed_recharge,
            fxRate: selectedRate.fx_rate,
            dateFrom: selectedRate.valid_from
        });

        console.log('Calculation Result:', {
            ctcInGBP,
            markupCost,
            fixedRecharge: selectedRule.fixed_recharge,
            costToPortCo
        });
    };

    const clearAll = () => {
        setCtc('');
        setPortfolioCompany(''); // Clear the Portfolio Company dropdown
        setSelectedRate(null); // Reset to default FX rate
        setResult(null);
        setError(''); // Clear any error messages
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    };

    const handleCtcChange = (e) => {
        const value = e.target.value.replace(/,/g, ''); // Remove commas for parsing
        setCtc(value);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            calculate(); // Trigger calculation on Enter key press
        }
    };

    return (
        <div className="container">
            <img src="/logo.png" alt="Blenheim Chalcot" style={{ width: '50%', marginBottom: '20px' }} />
            <h2>Calculate Cost to PortCo</h2>
            {loading && (
                <div className="loading-message">
                    Loading data, please wait...
                </div>
            )}
            {error && (
                <div className="error-message" style={{ color: 'red', margin: '10px 0' }}>
                    {error}
                </div>
            )}
            <input
                type="text"
                value={ctc.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} // Add thousands separator
                onChange={handleCtcChange}
                onKeyPress={handleKeyPress} // Handle Enter key press
                placeholder="Enter CTC"
            />
            <div className="dropdown-container">
                <select 
                    className="portfolio-dropdown" 
                    value={portfolioCompany} 
                    onChange={(e) => {
                        const selectedValue = e.target.value;
                        console.log('Portfolio Company Selected:', {
                            value: selectedValue,
                            matchingRules: portcoRules.filter(rule => rule.portco_name === selectedValue)
                        });
                        setPortfolioCompany(selectedValue);
                    }}
                >
                    <option value="">Select Portfolio Company</option>
                    {companies && companies.length > 0 ? (
                        companies.map((company) => (
                            <option key={company.id} value={company.portco_name}>
                                {company.portco_name}
                            </option>
                        ))
                    ) : (
                        <option value="" disabled>No companies available</option>
                    )}
                </select>
                <select 
                    className="fx-dropdown" 
                    value={selectedRate ? selectedRate.rate_pair : ""} 
                    onChange={(e) => {
                        console.log('Selected FX Rate:', e.target.value);
                        const selected = fxRates.find(rate => rate.rate_pair === e.target.value);
                        setSelectedRate(selected || null);
                    }}
                >
                    <option value="">Select FX Rate</option>
                    {fxRates && fxRates.length > 0 ? (
                        fxRates.map((rate, index) => {
                            console.log('Rendering rate option:', rate);
                            return (
                                <option key={rate.id || index} value={rate.rate_pair}>
                                    {rate.rate_pair}
                                </option>
                            );
                        })
                    ) : (
                        <option value="" disabled>No FX rates available</option>
                    )}
                </select>
            </div>
            <button className="calculate-button" onClick={calculate}>Calculate</button>
            {result && ( // Show the Clear All button only if there is a result
                <div>
                    <h3>Calculation Results:</h3>
                    <p>CTC in GBP: {formatCurrency(result.ctcInGBP)}</p>
                    <p>FX Rate (since {new Date(result.dateFrom).toLocaleDateString()}): {result.fxRate}</p>
                    <p>Fixed Recharge: {formatCurrency(result.fixedRecharge)}</p>
                    <p>Markup: {formatCurrency(result.markupCost)}</p>
                    <p>Cost to PortCo (annual): {formatCurrency(result.costToPortCo)}</p>
                    <p>Cost to PortCo (monthly): {formatCurrency(result.costToPortCo / 12)}</p>
                    <button className="clear-button" onClick={clearAll}>Clear All</button> {/* Move Clear All button here */}
                </div>
            )}
        </div>
    );
};

export default Calculator; 